import _, {pick} from 'lodash';
import * as mime from 'mime-types';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {isMobile} from '../../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {CustomError, ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {hasProperty} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {readApiModel} from '../../../helpers/apiHelpers';
import {
  CardAttributeUpdateAction,
  CardAttributeUpdateActionsEnum,
  cardAttributeUpdateReducer,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {
  filterContactsConnectedToCardIds,
  removeCardIdsFromContact,
} from '../../../helpers/models/contactHelpers';
import {globalNamespace} from '../../../helpers/models/namespaceHelpers';
import {ActionWithPayload, batch} from '../../../helpers/reduxHelpers';
import {CardGroupModel} from '../../../models/api/cardGroupModel';
import {ApiCard, CardModel, CardTypesEnum, Link} from '../../../models/api/cardModel';
import {buildContactFromContactUid, ContactModel, ContactSourcesEnum} from '../../../models/api/contactModel';
import {CustomFieldAttribute} from '../../../models/api/customFieldAttributeModel';
import {
  ApiCardConversationsDataGroup,
  ApiCardDataGroup,
  ApiCardPageDataGroup,
} from '../../../models/api/dataGroupModel';
import {ExportCardsJobOptionTypesEnum} from '../../../models/api/jobModel';
import {ApiPreviewCardMerge, PreviewCardMergeModel} from '../../../models/api/previewCardMergeModel';
import {
  ApiRecentConversations,
  parseRecentConversationsFromDataGroup,
  RecentConversationsModel,
} from '../../../models/api/recentConversationsModel';
import {
  ApiSearchCards,
  ApiSearchNamespacedCards,
  SearchCardsModel,
} from '../../../models/api/searchCardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {
  ApiSendOptions,
  fetchApiAsync,
  postApiAsync,
  postAsync,
  putApiAsync,
} from '../../../services/http/http';
import {HttpHeaders, ServerResponseInfo} from '../../../services/http/httpBase';
import {HttpError, HttpResponseError, isTransientError} from '../../../services/http/httpError';
import {findCardLock} from '../../../services/locks/resourceLocks';
import {Dispatch} from '../../appStore';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {
  showContactDetailsErrorBannerAction,
  showContactDetailsUndoBannerAction,
} from '../../panels/app/banners/bannersActions';
import {ContactDetailsErrorTypesEnum} from '../../panels/app/banners/bannersState';
import {ContactDetailsPanelId} from '../../panels/panelStateBase';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getFlatContacts} from '../dataSelectors';
import {DataState} from '../dataState';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Interfaces.
 */

export interface CreateCardTemplate {
  namespace: string;
  name: string;
  contacts: ReadonlyArray<ContactModel>;
  links?: ReadonlyArray<string>;
  customFieldAttributes?: ReadonlyArray<CustomFieldAttribute>;
  accountId?: number;
  cardGroupIds?: ReadonlyArray<number>;
  // By default it will create an enhanced card, but is AUTO is specified it will create an auto card
  type?: CardTypesEnum;
}

export interface UpdateCardOptions {
  type?: CardTypesEnum.ENHANCED; // A card's type can only be updated to enhanced
  name?: string;
  description?: string;
  links?: ReadonlyArray<Link>;
  contacts?: ReadonlyArray<ContactInfoPutInterface>;
  custom_field_attributes?: ReadonlyArray<OverrideMergeCustomFieldAttributePayload>;
  groups?: ReadonlyArray<UpdateCardGroupValues>;
  account_id?: number | null;
}

export interface UpdateCardGroupValues {
  id: number;
}

export interface DuplicateCardOptions {
  namespace: string;
}

export interface OverrideMergeValues {
  name?: string;
  accountId?: number;
  customFieldAttributes?: {
    [T: number]: string;
  };
}
export interface ContactInfoPutInterface {
  handle: string;
  source: ContactSourcesEnum;
}

export interface OverrideMergeCustomFieldAttributePayload {
  // eslint-disable-next-line camelcase
  custom_field_id: number;
  value: string;
}

interface OverrideMergeValuesPayload {
  name?: string;
  // eslint-disable-next-line camelcase
  account_id?: number;
  // eslint-disable-next-line camelcase
  custom_fields?: OverrideMergeCustomFieldAttributePayload[];
}

export interface FetchCardsByNamespaceResponse {
  cards: ReadonlyArray<CardModel>;
  cardGroups: ReadonlyArray<CardGroupModel>;
  contacts: ReadonlyArray<ContactModel>;
  limit: number;
}

export interface FetchCardsByQueryResponse {
  cards: ReadonlyArray<CardModel>;
  contacts: ReadonlyArray<ContactModel>;
  limit: number;
}

export interface FetchCardsByNamespaceOptions extends ApiSendOptions {
  limit?: number;
  offset?: number;
  auto?: boolean;
  namespace?: string;
}

export interface FetchCardsByQueryOptions extends ApiSendOptions {
  query: string;
  limit?: number;
  offset?: number;
}

export class CreateCardConflictError extends CustomError {
  constructor(conflictingCard: CardModel) {
    super('The card could not be created because of a conflicting card.');
    this.conflictingCard = conflictingCard;
  }

  readonly conflictingCard: CardModel;
}

/*
 * Action types.
 */

export enum CardActionsEnum {
  CARDS_DELETED = '@@data/cardDeleted/CARDS_DELETED',
}

/*
 * Action payloads.
 */

interface CardsDeletedActionPayload {
  cardIds: ReadonlyArray<number>;
}

interface CardsDeletedAction extends ActionWithPayload<CardsDeletedActionPayload> {
  type: CardActionsEnum.CARDS_DELETED;
}

/*
 * Action creators.
 */

export function cardsDeletedAction(cardIds: ReadonlyArray<number>): CardsDeletedAction {
  return {
    type: CardActionsEnum.CARDS_DELETED,
    payload: {
      cardIds,
    },
  };
}

/*
 * Union of actions.
 */

export type CardAction = CardsDeletedAction;

/*
 * Actions.
 */

interface UriFile {
  name: string;
  uri: string;
}

function buildUpdateCardAvatarOptions(options?: ApiSendOptions | undefined) {
  if (!isMobile()) {
    return options;
  }
  const headers: HttpHeaders = {
    'Content-Type': 'multipart/form-data',
    'X-Front-Content-Type': 'multipart/form-data',
  };

  // Combine with the provided options.
  return {
    ...options,
    headers: {
      ...(options && options.headers),
      ...headers,
    },
  };
}

export function updateCardAvatarAsyncAction(
  card: CardModel,
  file: File | UriFile,
  options?: ApiSendOptions,
): ThunkAsyncAction<void> {
  return async (dispatch) => {
    const updateAvatarEndpoint = `${card.url}/avatar`;

    const formData = new FormData();
    if ('uri' in file) {
      const type = mime.lookup(file.name) || 'application/octet-stream';
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      formData.append('avatar', {
        type,
        uri: file.uri,
        name: file.name,
      } as any);
      formData.append('namespace', card.namespace);
    } else {
      formData.append('avatar', file);
      formData.append('namespace', card.namespace);
    }

    try {
      const updatedCard = await putApiAsync(
        ApiCard,
        updateAvatarEndpoint,
        formData,
        buildUpdateCardAvatarOptions(options),
      );
      dispatch(
        updateDataAction({
          cards: [updatedCard],
        }),
      );
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

export function searchForCardsAsyncAction(
  query: string,
  options?: ApiSendOptions,
): ThunkAsyncAction<SearchCardsModel | undefined> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;

    // TODO: Add limit support when the backend api is fixed
    const searchCardsUrl = addQueryToPath(
      `${company.url}/search_card/${encodeURIComponent(query.toLowerCase())}`,
      {
        limit: 20,
      },
    );

    try {
      return await fetchApiAsync(ApiSearchCards, searchCardsUrl, options);
    } catch (error) {
      return undefined;
    }
  };
}

export function fetchCardsForContactAsyncAction(
  contact: ContactModel,
  options?: ApiSendOptions,
): ThunkAsyncAction<ReadonlyArray<CardModel>> {
  return async (dispatch) => {
    if (!contact.url) {
      return [];
    }

    const cardsUrl = `${contact.url}/cards`;

    try {
      const cardsDataGroup = await fetchApiAsync(ApiCardPageDataGroup, cardsUrl, options);
      dispatch(updateDataAction(cardsDataGroup));
      return cardsDataGroup.cards || [];
    } catch (error) {
      // If it is transient error we can just rethrow that error as it was not contact specific
      if (isTransientError(error)) {
        throw error;
      }

      throw new ErrorWithData('Could not fetch cards for contact', {
        uid: contact.uid,
        handle: contact.handle,
        error,
      });
    }
  };
}

export function fetchCardConversationsAsyncAction(
  card: CardModel,
  options?: ApiSendOptions,
  limit?: number,
): ThunkAsyncAction<RecentConversationsModel> {
  return async (dispatch) => {
    const conversationsUrl = addQueryToPath(`${card.url}/conversations`, {limit});

    try {
      const cardConversationsDataGroup = await fetchApiAsync(
        ApiCardConversationsDataGroup,
        conversationsUrl,
        options,
      );
      dispatch(updateDataAction(cardConversationsDataGroup));
      return parseRecentConversationsFromDataGroup(cardConversationsDataGroup);
    } catch (error) {
      // If it is transient error we can just rethrow that error as it was not card specific
      if (isTransientError(error)) {
        throw error;
      }

      throw new ErrorWithData('Failed to fetch conversations for card', {
        id: card.id,
      });
    }
  };
}

export function fetchContactConversationsAsyncAction(
  contact: ContactModel,
  options?: ApiSendOptions,
  limit?: number,
): ThunkAsyncAction<RecentConversationsModel> {
  return async (dispatch) => {
    if (!contact.url) {
      return {
        conversations: [],
        hasMore: false,
      };
    }

    const conversationsUrl = addQueryToPath(`${contact.url}/conversations`, {limit});

    try {
      const contactConversations = await fetchApiAsync(ApiRecentConversations, conversationsUrl, options);
      dispatch(updateDataAction({conversations: contactConversations.conversations}));
      return contactConversations;
    } catch (error) {
      // If it is transient error we can just rethrow that error as it was not contact specific
      if (isTransientError(error)) {
        throw error;
      }

      throw new ErrorWithData('Failed to fetch conversations for contact', {
        id: contact.uid,
      });
    }
  };
}

export function createCardActionAsync(template: CreateCardTemplate): ThunkAsyncAction<CardModel> {
  return async (dispatch, getState) => {
    const {company} = getState().data;

    const {namespace, name, contacts, type, customFieldAttributes, links, accountId, cardGroupIds} = template;
    const endpoint = `${company.url}/namespaces/${namespace}/cards`;
    const body = {
      namespace,
      name,
      contacts: contacts.map((c) => pick(c, 'handle', 'source')),
      type,
      custom_field_attributes:
        customFieldAttributes?.map((attribute) => ({
          custom_field_id: attribute.id,
          value: attribute.value,
        })) ?? [],
      links:
        links?.map((link) => ({
          url: link,
        })) ?? [],
      account_id: accountId,
      group_ids: cardGroupIds,
    };

    try {
      const cardDataGroup = await postApiAsync(ApiCardDataGroup, endpoint, body);
      const {cards} = cardDataGroup;

      dispatch(updateDataAction(cardDataGroup));

      if (!cards || !cards[0]) {
        throw new Error('No card was created');
      }

      return cards[0];
    } catch (error) {
      // We're only interested in conflicts with another card.
      if (!(error instanceof HttpResponseError) || error.status !== 409 || !hasProperty(error.body, 'card')) {
        throw error;
      }

      // When that happens, parse the other card and add it to the state.
      const conflictingCardDataGroup = readApiModel(ApiCardDataGroup, error.body.card);
      const {cards} = conflictingCardDataGroup;

      dispatch(updateDataAction(conflictingCardDataGroup));

      if (!cards || !cards[0]) {
        throw new Error('No conflicting card was found.');
      }

      throw new CreateCardConflictError(cards[0]);
    }
  };
}

export function updateCardAsyncAction(
  cardUrl: string,
  options: UpdateCardOptions,
): ThunkAsyncAction<CardModel> {
  return async (dispatch) => {
    const result = await putApiAsync(ApiCardDataGroup, cardUrl, options);

    dispatch(updateDataAction(result));

    const {cards} = result;
    if (!cards || !cards[0]) {
      throw new Error('No card was updated');
    }

    return cards[0];
  };
}

export function updateCardsToEnhancedAsyncAction(cards: ReadonlyArray<CardModel>): ThunkAsyncAction {
  return async (dispatch) => {
    try {
      const updateCardPromises = cards.map((card) =>
        putApiAsync(ApiCardDataGroup, card.url, {name: card.name, type: CardTypesEnum.ENHANCED}),
      );
      const responses = await Promise.all(updateCardPromises);
      batch(() => {
        responses.forEach((response) => dispatch(updateDataAction(response)));
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function duplicateOrFetchDuplicateCardsAsyncAction(
  cards: ReadonlyArray<CardModel>,
  options: DuplicateCardOptions,
): ThunkAsyncAction<ReadonlyArray<CardModel>> {
  return async (dispatch) => {
    try {
      const duplicateCardPromises = cards.map((card) =>
        postApiAsync(ApiCardDataGroup, `${card.url}/duplicate`, options),
      );

      // Cards can only be duplicated once. If we get a conflict, catch the error
      // so we can parse it later to get the conflicting card.
      const allResponses = await Promise.all(
        duplicateCardPromises.map((promise) =>
          promise
            .then((result) => ({
              status: 'fulfilled',
              result,
            }))
            .catch((error) => {
              // Only proceed for 409 Conflict error
              if (error.status !== 409) {
                throw error;
              }
              return {
                status: 'rejected',
                result: error,
              };
            }),
        ),
      );

      batch(() => {
        allResponses.forEach((response) => {
          if (response.status === 'fulfilled') {
            dispatch(updateDataAction(response.result));
          }
        });
      });

      return allResponses.reduce<ReadonlyArray<CardModel>>((acc, response) => {
        if (response.status === 'fulfilled') {
          return acc.concat(response.result.cards);
        }
        return acc.concat([response.result.body.card]); // add the card from the 409 conflict error body
      }, []);
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function duplicateCardActionAsync(
  card: CardModel,
  options: DuplicateCardOptions,
): ThunkAsyncAction<CardModel> {
  return async (dispatch) => {
    try {
      const result = await postApiAsync(ApiCardDataGroup, `${card.url}/duplicate`, options);
      dispatch(updateDataAction(result));
      const {cards} = result;
      if (!cards || !cards[0]) {
        throw new Error('No card was duplicated');
      }
      return cards[0];
    } catch (error) {
      // We're only interested in conflicts with another card.
      if (error instanceof HttpResponseError && error.status === 409 && hasProperty(error.body, 'cards')) {
        throw new ErrorWithData('Failed duplicating the card', {cards: error.body.cards});
      } else {
        throw error;
      }
    }
  };
}

export function updateCardAction(
  newCard: CardModel,
  shouldOnlyUpdateIfCurrentlyExists: boolean = false,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing card.
    const existingCard = getState().data.cards[newCard.id];

    // No need to do anything if the new card is older.
    if (existingCard && existingCard.updatedAt > newCard.updatedAt) {
      return null;
    }

    // Only update if we have an existing card and flag is set
    if (shouldOnlyUpdateIfCurrentlyExists && !existingCard) {
      return null;
    }

    // Otherwise, update the card.
    return dispatch(updateDataAction({cards: [newCard], updatedAt: newCard.updatedAt}));
  };
}

export function fetchPreviewCardMergeAsyncAction(
  mainCardId: number,
  cardIds: ReadonlyArray<number>,
  options?: ApiSendOptions,
): ThunkAsyncAction<PreviewCardMergeModel> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const previewUrl = addQueryToPath(`${company.url}/card_batch/merge_preview`, {
      main_card_id: mainCardId,
      card_ids: cardIds,
    });

    try {
      const previewCardMerge = await fetchApiAsync(ApiPreviewCardMerge, previewUrl, options);
      return previewCardMerge;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function mergeCardsAsyncAction(
  mainCardId: number,
  cardIds: ReadonlyArray<number>,
  overrides?: OverrideMergeValues,
): ThunkAsyncAction<CardModel> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const mergeUrl = `${company.url}/card_batch/merge`;

    try {
      return await postApiAsync(ApiCard, mergeUrl, {
        main_card_id: mainCardId,
        card_ids: cardIds,
        overrides: convertOverrideMergeValuesToPayload(overrides),
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

function convertOverrideMergeValuesToPayload(
  overrides?: OverrideMergeValues,
): OverrideMergeValuesPayload | undefined {
  if (!overrides) {
    return undefined;
  }
  return {
    name: overrides.name,
    account_id: Number(overrides.accountId),
    custom_fields: buildCustomFieldsPayload(overrides.customFieldAttributes),
  };
}

function buildCustomFieldsPayload(
  customFieldAttributes: Record<number, string> | undefined,
): OverrideMergeCustomFieldAttributePayload[] | undefined {
  if (!customFieldAttributes) {
    return undefined;
  }

  return _(customFieldAttributes)
    .keys()
    .map(Number)
    .reduce((customOverrides: OverrideMergeCustomFieldAttributePayload[], customFieldId) => {
      const value = customFieldAttributes[customFieldId];
      if (!value) {
        return customOverrides;
      }
      return [
        ...customOverrides,
        {
          custom_field_id: customFieldId,
          value,
        },
      ];
    }, []);
}

export interface UpdateCardAttributeAsyncActionParams {
  panelId: ContactDetailsPanelId;
  card: CardModel;
  action: CardAttributeUpdateAction;
  shouldShowUndoBanner: boolean;
}

export function updateCardAttributeAsyncAction(
  params: UpdateCardAttributeAsyncActionParams,
): ThunkAsyncAction<void> {
  const {card, action} = params;
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'cards'>(
      'cards',
      createResourceMapValueUpdater(
        card.id,
        createUpdater<CardModel>((currentCard) => cardAttributeUpdateReducer(currentCard, action)),
      ),
    );
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCardLock(card.id).lockAsync(async () => {
          try {
            const updatedCard = cardAttributeUpdateReducer(card, action);
            const payload = computePayloadForCardAttributeUpdate(updatedCard);
            const result = await putApiAsync(ApiCardDataGroup, card.url, payload);
            maybeShowUndoBanner(dispatch, params);
            return updateDataAction(result);
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }
            // Conflicting handle with another card
            if (error.status === 409 && hasProperty(error.body, 'card')) {
              maybeShowHandleTakenErrorBanner(
                dispatch,
                card,
                action,
                error.body.card as CardModel,
                params.panelId,
              );
              throw error;
            }
            // Too many handles in a card.
            if (error.status === 400 && error.reason?.includes('Limit Exceeded')) {
              maybeShowTooManyHandlesErrorBanner(dispatch, action, params.panelId);
            }
            return undefined;
          }
        }),
      ),
    );
  };
}

export function showContactDetailsMergeErrorBannerAction(
  panelId: ContactDetailsPanelId,
  errorType: ContactDetailsErrorTypesEnum,
  mainCard?: CardModel,
) {
  return showContactDetailsErrorBannerAction(panelId, errorType, {mainCard});
}

export function fetchCardsByNamespaceAsyncAction(
  options: FetchCardsByNamespaceOptions,
): ThunkAsyncAction<FetchCardsByNamespaceResponse> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const namespace = options?.namespace || globalNamespace;
    const namespaceSearchUrl = addQueryToPath(`${companyUrl}/namespaces/${namespace}/cards`, {
      auto: options?.auto || false,
      limit: options?.limit,
      offset: options?.offset,
    });

    try {
      const {cards, contacts, cardGroups, limit} = await fetchApiAsync(
        ApiSearchNamespacedCards,
        namespaceSearchUrl,
        options,
      );
      return {
        cards,
        contacts,
        cardGroups,
        limit,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchCardsByQueryAsyncAction(
  options: FetchCardsByQueryOptions,
): ThunkAsyncAction<FetchCardsByQueryResponse> {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const {query} = options;
    const searchCardsUrl = addQueryToPath(
      `${companyUrl}/search_card/${encodeURIComponent(query.toLowerCase())}`,
    );

    try {
      const {cards, contacts, limit} = await fetchApiAsync(ApiSearchCards, searchCardsUrl, options);
      return {
        cards,
        contacts,
        limit,
      };
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteCardsAsyncAction(cardIds: ReadonlyArray<number>): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const deleteUrl = `${companyUrl}/card_batch/delete`;
    const body = {
      card_ids: cardIds,
    };

    try {
      await postAsync(deleteUrl, body);

      const contacts = getFlatContacts(getState());
      // Map through all the contacts to remove the deleted cards from the contacts.
      const updatedContacts = filterContactsConnectedToCardIds(contacts, cardIds).map((contact) =>
        removeCardIdsFromContact(contact, cardIds),
      );

      batch(() => {
        dispatch(cardsDeletedAction(cardIds));
        if (updatedContacts.length > 0) {
          dispatch(updateDataAction({contacts: updatedContacts}));
        }
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function fetchCardAsyncAction(cardId: number): ThunkAsyncAction<CardModel> {
  return async (dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const url = addQueryToPath(`${companyUrl}/cards/${cardId}`);

    try {
      const card = await fetchApiAsync(ApiCard, url);
      dispatch(updateDataAction({cards: [card]}));
      return card;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function uploadContactsCsvAsyncAction(
  namespace: string,
  csvFile: File,
  exportError: boolean = false,
): ThunkAsyncAction<undefined | File> {
  return async (_dispatch, getState) => {
    const {company} = getState().data;
    const contactsCSVUploadUrl = addQueryToPath(
      `${company.url}/namespaces/${namespace}/contacts/csv_contacts`,
      {
        export_error: exportError,
      },
    );

    try {
      let serverResponseInfo: ServerResponseInfo | undefined;
      const onResponseReceived = (info: ServerResponseInfo) => {
        serverResponseInfo = info;
      };

      const formData = new FormData();
      formData.append('file', csvFile);
      const response = await postAsync(contactsCSVUploadUrl, formData, {
        headers: {'Content-Type': 'file-upload'},
        onResponseReceived,
      });

      if (!exportError) {
        return undefined;
      }

      // The content-disposition may contain the new file name.
      const fileName = serverResponseInfo?.headers['content-disposition']
        ?.split('filename="')[1]
        .slice(0, -1);

      // Construct the new file object with the response.
      return new File([new Blob([response], {type: 'text/csv'})], fileName ?? 'erred-file.csv', {
        type: 'text/csv',
      });
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function exportCardsAsyncAction(exportType: ExportCardsJobOptionTypesEnum): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    const companyUrl = getCompanyUrl(getState());
    const url = `${companyUrl}/card_exports`;

    try {
      await postAsync(url, {export_type: exportType});
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function createPrivateCardAsyncAction(
  card: CardModel | undefined,
  visualContact: VisualContactModel,
): ThunkAsyncAction<number> {
  return async (dispatch, getState) => {
    const userNamespace = getUserNamespace(getState());

    // If we do not have a card, we will need to create a new one.
    if (!card) {
      const createdCard = await dispatch(
        createCardActionAsync({
          namespace: userNamespace,
          name: visualContact.name || visualContact.contact.handle,
          contacts: [visualContact.contact],
        }),
      );
      return createdCard.id;
    }
    const duplicatedCard = await dispatch(duplicateCardActionAsync(card, {namespace: userNamespace}));
    return duplicatedCard.id;
  };
}

export function createCompanyCardAsyncAction(
  card: CardModel | undefined,
  visualContact: VisualContactModel,
): ThunkAsyncAction<number> {
  return async (dispatch) => {
    // If we do not have a card, we will need to create a new one.
    if (!card) {
      const createdCard = await dispatch(
        createCardActionAsync({
          namespace: globalNamespace,
          name: visualContact.name || visualContact.contact.handle,
          contacts: [visualContact.contact],
        }),
      );
      return createdCard.id;
    }
    await dispatch(updateCardAsyncAction(card.url, {type: CardTypesEnum.ENHANCED}));
    return card.id;
  };
}

/*
 * Helpers.
 */

function computePayloadForCardAttributeUpdate(card: CardModel) {
  return {
    name: card.name,
    description: card.description || '',
    links: card.links,
    contacts: card.contactUids.map(buildContactFromContactUid).map((contact) => ({
      source: contact.source,
      handle: contact.handle,
    })),
    custom_field_attributes: card.customFieldAttributes?.map((attribute) => ({
      custom_field_id: attribute.id,
      value: attribute.value,
    })),
    account_id: card.accountId || null,
  };
}

function maybeShowUndoBanner(dispatch: Dispatch, params: UpdateCardAttributeAsyncActionParams) {
  const {card, action, shouldShowUndoBanner, panelId} = params;
  if (!shouldShowUndoBanner) {
    return;
  }

  dispatch(showContactDetailsUndoBannerAction(panelId, card.id, action));
}

function maybeShowHandleTakenErrorBanner(
  dispatch: Dispatch,
  mainCard: CardModel,
  action: CardAttributeUpdateAction,
  conflictingCard: CardModel,
  contactDetailsPanelId: ContactDetailsPanelId,
) {
  if (
    action.type === CardAttributeUpdateActionsEnum.ADD_CONTACT ||
    action.type === CardAttributeUpdateActionsEnum.UPDATE_CONTACT
  ) {
    dispatch(
      showContactDetailsErrorBannerAction(contactDetailsPanelId, ContactDetailsErrorTypesEnum.HANDLE_TAKEN, {
        mainCard,
        conflictingContactUid: action.payload.contactUid,
        conflictingCard,
        contactDetailsPanelId,
      }),
    );
  }
}

function maybeShowTooManyHandlesErrorBanner(
  dispatch: Dispatch,
  action: CardAttributeUpdateAction,
  contactDetailsPanelId: ContactDetailsPanelId,
) {
  if (action.type === CardAttributeUpdateActionsEnum.ADD_CONTACT) {
    dispatch(
      showContactDetailsErrorBannerAction(
        contactDetailsPanelId,
        ContactDetailsErrorTypesEnum.TOO_MANY_HANDLES,
        {
          contactDetailsPanelId,
        },
      ),
    );
  }
}
